import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TabNav, TextBox, RadioButton, Button, Card } from '../../../components/common';
import { useCountryOptions } from '../../../constants/countries';
import { myPageTabs } from '../../../constants/uiConstants';
import { userService } from '../../../api/services';

export default function AccountEdit() {
  const { t } = useTranslation();
  const countryOptions = useCountryOptions();
  const genderOptions = [
    { value: 'male', label: t('mypage.gender_male') },
    { value: 'female', label: t('mypage.gender_female') },
  ];
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [gender, setGender] = useState('');
  const [phone, setPhone] = useState('');
  const [marketing, setMarketing] = useState('deny');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) { navigate('/login', { replace: true }); return; }
    userService.getProfile()
      .then((data) => {
        if (data.name) setName(data.name);
        if (data.country) setCountry(data.country);
        if (data.gender) setGender(data.gender);
        if (data.phone) {
          const digits = data.phone.replace(/\D/g, '');
          const code = (data.country_code || '').replace(/\D/g, '');
          setPhone(code ? `+${code}${digits.replace(/^0/, '')}` : data.phone);
        }
        setMarketing(data.marketing_consent ? 'allow' : 'deny');
      })
      .catch((e) => console.warn('[AccountEdit] 프로필 로드 실패:', e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await userService.updateProfile({ name, country, gender, phone, marketing_consent: marketing === 'allow' });
      navigate('/mypage/account');
    } catch {
      alert(t('mypage.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'reviews') {
      navigate('/mypage/reviews');
    }
  };

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* TabNav - fullwidth */}
      <TabNav variant="fullwidth" tabs={myPageTabs} activeTab="account" onTabChange={handleTabChange} />

      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-8 sm:py-12">
        {/* Page Title */}
        <h2 className="text-xl font-bold text-mono-900 mt-8 mb-6">{t('mypage.edit_title')}</h2>

        {/* Form Card */}
        <Card variant="padded">
          <TextBox
            label={t('mypage.name_label')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextBox
            label={t('mypage.country_label')}
            variant="select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder={t('mypage.select_country')}
            options={countryOptions}
          />

          <TextBox
            label={t('mypage.gender_label')}
            variant="select"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            placeholder={t('mypage.select_gender')}
            options={genderOptions}
          />

          <TextBox
            label={t('mypage.phone_label')}
            variant="phone"
            value={phone}
            onChange={(value) => setPhone(value ?? '')}
          />
        </Card>

        {/* Marketing Section */}
        <Card variant="padded" className="mt-4">
          <label className="text-2sm font-medium text-mono-600 block mb-3">{t('mypage.marketing_consent')}</label>
          <div className="flex items-center gap-6">
            <RadioButton
              name="marketing"
              value="allow"
              label={t('mypage.marketing_allowed')}
              checked={marketing === 'allow'}
              onChange={() => setMarketing('allow')}
            />
            <RadioButton
              name="marketing"
              value="deny"
              label={t('mypage.marketing_denied')}
              checked={marketing === 'deny'}
              onChange={() => setMarketing('deny')}
            />
          </div>
        </Card>

        {/* Bottom Buttons */}
        <div className="flex gap-3 mt-8">
          <Button level="level3" fullWidth onClick={() => navigate('/mypage/account')}>{t('common.cancel')}</Button>
          <Button level="level1" fullWidth onClick={handleSave} disabled={saving || loading}>{saving ? t('common.loading') : t('common.edit')}</Button>
        </div>
      </div>
    </div>
  );
}
